import React, { useState } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../../tailwindComponents/ui/tabs"
import { CategoryLevelItem } from '../../../interfaces/dashboards/PerformanceDashboard.interface';
import BasicTable from '../../../tailwindComponents/tables/basic-table.component';
import PDS from '../../../services/dashboards/PerformanceDashboard.service';
import { CircleAlert, TrendingDown, TrendingUp } from 'lucide-react';

interface InventoryHealthProps {
    overstockedClasses: CategoryLevelItem[];
    understockedClasses: CategoryLevelItem[];
}

interface MissingSalesClasses {
    className: string;
    category: string;
    missedSales: number;
}

function RedFormatting(value: number | string): JSX.Element {
    return <span className="text-red-500">{value}</span>;
}

export default function InventoryHealth({ overstockedClasses, understockedClasses }: InventoryHealthProps) {
    const [selectedInventoryHealthTab, setSelectedInventoryHealthTab] = useState('overstocked'); 
    const [limitItems, setLimitItems] = useState(5);

    const [parsedOverstockedClasses, setParsedOverstockedClasses] = useState(overstockedClasses);
    const [parsedUnderstockedClasses, setParsedUnderstockedClasses] = useState(understockedClasses);
    const [missingSalesClasses, setMissingSalesClasses] = useState<MissingSalesClasses[]>([]);

    React.useEffect(() => {
        let _missingSalesClasses = understockedClasses.map((item) => ({
            className: item.className,
            category: item.category,
            missedSales: PDS.calculateMissedSales(item.inventoryBomPlan, item.inventoryBomRm, item.salesRm)
        })).filter((item) => item.missedSales != 0);
        if (limitItems !== 999) {
            setParsedOverstockedClasses(
                overstockedClasses
                    .sort((a, b) => (b.inventoryBomRm - b.inventoryBomPlan) - (a.inventoryBomRm - a.inventoryBomPlan)) // Descending sort
                    .slice(0, limitItems)
            );
            setParsedUnderstockedClasses(
                understockedClasses
                    .sort((a, b) => (a.inventoryBomRm - a.inventoryBomPlan) - (b.inventoryBomRm - b.inventoryBomPlan)) // Ascending sort
                    .slice(0, limitItems)
            );
            setMissingSalesClasses(
                _missingSalesClasses
                    .sort((a, b) => a.missedSales - b.missedSales)
                    .slice(0, limitItems)
            );
        } else {
            setParsedOverstockedClasses(overstockedClasses);
            setParsedUnderstockedClasses(understockedClasses);
            setMissingSalesClasses(_missingSalesClasses);
        }
    }, [limitItems, overstockedClasses, understockedClasses]);

    return (
        <div className="flex flex-col shadow-xl p-5 rounded-xl border border-gray-300 bg-white">
            <div className="flex flex-row justify-between items-center mb-4">
                <h1 className="text-2xl font-bold tracking-tight">Inventory Health and Opportunities</h1>
                <div className="flex flex-row items-center">
                    <select
                        id="limitItems"
                        value={limitItems}
                        onChange={(e) => setLimitItems(Number(e.target.value))}
                        className="border border-gray-300 rounded p-2"
                    >
                        <option value={999}>All items</option>
                        <option value={5}>Top 5 items</option>
                        <option value={10}>Top 10 items</option>
                    </select>
                </div>
            </div>
            <Tabs defaultValue="overstocked" className="w-full">
            <TabsList className="flex flex-row overflow-x-auto overflow-y-hidden whitespace-nowrap bg-gray-200 rounded-xl">
                    <TabsTrigger
                        value="overstocked"
                        className="flex-1 text-center border-b-2 hover:border-gray-300 data-[state=active]:bg-white data-[state=active]:text-black rounded-lg"
                        onClick={() => setSelectedInventoryHealthTab('overstocked')}
                    >
                        <TrendingUp className="mr-2 h-5 w-5" />
                        Overstocked
                    </TabsTrigger>
                    <TabsTrigger
                        value="understocked"
                        className="flex-1 text-center border-b-2 hover:border-gray-300 data-[state=active]:bg-white data-[state=active]:text-black rounded-lg"
                        onClick={() => setSelectedInventoryHealthTab('understocked')}
                    >
                        <TrendingDown className="mr-2 h-5 w-5" />
                        Understocked
                    </TabsTrigger>
                    <TabsTrigger
                        value="missedSales"
                        className="flex-1 text-center border-b-2 hover:border-gray-300 data-[state=active]:bg-white data-[state=active]:text-black rounded-lg"
                        onClick={() => setSelectedInventoryHealthTab('missedSales')}
                    >
                        <CircleAlert className="mr-2 h-5 w-5" />
                        Missed Sales
                    </TabsTrigger>
                </TabsList>
                <TabsContent value="overstocked" className="mt-4 overflow-y-auto max-h-[500px]">
                        <BasicTable
                            columns={['Class (Category)', 'Actual Inventory', 'Planned Inventory',
                                {name: 'Overstock', customFormatting: RedFormatting},
                                'Freshness', 'Weeks on Hand'
                            ]}
                            data={parsedOverstockedClasses?.map((item) => [
                                `${item.className} (${item.category})`,
                                PDS.moneyFormat(item.inventoryBomRm),
                                PDS.moneyFormat(item.inventoryBomPlan),
                                PDS.moneyFormat(item.inventoryBomRm - item.inventoryBomPlan),
                                `${PDS.percentageFormat(PDS.calculateFreshness(item.inflowRetailRm, item.inventoryBomRm) / 100)}`,
                                `${PDS.calculateWeeksOnHand(item.salesSumLm, item.inventoryBomAvgLm)}`
                            ])}
                            rowClassName={(rowIndex) => (rowIndex % 2 === 0 ? 'bg-gray-100' : '')}
                            defaultSortColumn={'Overstock'}
                            defaultSortDirection="desc"
                        />
                </TabsContent>
                <TabsContent value="understocked" className="mt-4 min-h-[500px] h-[500px] overflow-y-auto">
                    <BasicTable
                        columns={['Class (Category)', 'Actual Inventory', 'Planned Inventory',
                            {name: 'Understock', customFormatting: RedFormatting},
                            'Freshness', 'Weeks on Hand'
                        ]}
                        data={parsedUnderstockedClasses?.map((item) => [
                            `${item.className} (${item.category})`,
                            PDS.moneyFormat(item.inventoryBomRm),
                            PDS.moneyFormat(item.inventoryBomPlan),
                            PDS.moneyFormat(item.inventoryBomRm - item.inventoryBomPlan),
                            `${PDS.percentageFormat(PDS.calculateFreshness(item.inflowRetailRm, item.inventoryBomRm) / 100)}`,
                            `${PDS.calculateWeeksOnHand(item.salesSumLm, item.inventoryBomAvgLm)}`
                        ])}
                        rowClassName={(rowIndex) => (rowIndex % 2 === 0 ? 'bg-gray-100' : '')}
                        defaultSortColumn={'Understock'}
                        defaultSortDirection="asc"
                    />
                </TabsContent>
                <TabsContent value="missedSales" className="mt-4 max-h-[500px] overflow-y-auto">
                    <BasicTable
                        columns={['Class (Category)', {name: 'Missed Sales', customFormatting: RedFormatting}]}
                        data={missingSalesClasses?.map((item) => [
                            `${item.className} (${item.category})`,
                            `${PDS.moneyFormat(item.missedSales)}`
                        ])}
                        rowClassName={(rowIndex) => (rowIndex % 2 === 0 ? 'bg-gray-100' : '')}
                        defaultSortColumn={'Missed Sales'}
                        defaultSortDirection="asc"
                    />
                </TabsContent>
            </Tabs>
        </div>
    )
}